import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { influencers, negotiations } from 'api';
import { DetailsLayout, Loader } from 'components';
import Header from './components/Header';
import CalculatorTab from './components/CalculatorTab';
import Actions from './components/Header/Actions';
import PageTabs from './components/PageTabs';

type Params = 'id';

interface Props {
  itemId?: string;
}

const Details = (props: Props): JSX.Element => {
  const { itemId: propsId } = props;
  const { id: paramsId = '' } = useParams<Params>();

  const id = useMemo(() => propsId || paramsId, [propsId, paramsId]);

  const { data, refetch } = useQuery(
    [negotiations.endpoints.getById(id)],
    () => negotiations.getById(id),
    { enabled: !!id },
  );

  const { data: influencer } = useQuery(
    [influencers.endpoints.getById(data?.influencer_id?.$oid || '')],
    () => influencers.getById(data?.influencer_id?.$oid || ''),
    {
      enabled: !!data,
    },
  );

  if (!data) {
    return <Loader variant="centered" height="80vh" />;
  }

  if (propsId) {
    return (
      <Stack>
        <Box
          sx={{
            p: '11.5px',
            position: 'sticky',
            top: 0,
            width: '100%',
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            background: (t) => t.palette.background.paper,
            zIndex: (t) => t.zIndex.drawer + 1,
          }}
        >
          <Actions negotiation={data} refetch={refetch} inline />
        </Box>
        <Box p={2} sx={{ overflowX: 'hidden' }}>
          <Header
            negotiation={data}
            refetch={refetch}
            influencer={influencer}
            inline
          />
          <PageTabs data={data} influencer={influencer} inline />
        </Box>
      </Stack>
    );
  }

  return (
    <DetailsLayout
      drawer={<CalculatorTab negotiation={data} influencer={influencer} />}
    >
      <Stack p={3}>
        <Header negotiation={data} refetch={refetch} influencer={influencer} />
        <Box py={3} sx={{ overflowX: 'hidden' }}>
          <PageTabs data={data} influencer={influencer} />
        </Box>
      </Stack>
    </DetailsLayout>
  );
};

export default Details;
